import React from "react";
import { Link } from "react-router";
import { ArrowRight, Headphones, Loader2, Lock, Video } from "lucide-react";
import { groupMediaByProvider } from "../../hooks/useTopicMedia";
import TopicLibraryEmptyState from "../guest/TopicLibraryEmptyState";
import { foregroundOnSolidAccent } from "../../pages/guest_pages/subcategory_pages/shared/categoryDetailShared";
import MediaEmbedPlayer from "./MediaEmbedPlayer";

function MediaItemCard({ item, color, variant }) {
  return (
    <article className="flex h-full min-w-0 flex-col overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-sm transition hover:shadow-[0_16px_40px_-32px_rgba(0,0,0,0.18)]">
      <MediaEmbedPlayer item={item} color={color} variant={variant} />
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        {item.providerName ? (
          <p className="mb-1 text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">
            {item.providerName}
          </p>
        ) : null}
        <h3 className="text-base font-black leading-snug text-gray-950">{item.title}</h3>
        {item.description ? (
          <p className="mt-2 text-sm leading-relaxed text-gray-500">{item.description}</p>
        ) : null}
      </div>
    </article>
  );
}

function ProviderMediaGroup({ group, color, variant }) {
  const showProviderHeading = group.providerName && group.providerName !== "Featured providers";
  return (
    <div className="space-y-4">
      {showProviderHeading ? (
        <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">{group.providerName}</p>
      ) : null}
      <div className="grid gap-5 sm:grid-cols-2">
        {group.items.map((item) => (
          <MediaItemCard key={item.id} item={item} color={color} variant={variant} />
        ))}
      </div>
    </div>
  );
}

function MediaSection({ title, icon: Icon, items, color, variant, emptyHint }) {
  if (!items.length) return null;

  const groups = groupMediaByProvider(items);

  return (
    <section className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ background: `${color}18`, color }}
          >
            <Icon size={18} strokeWidth={2.25} />
          </span>
          <div>
            <h3 className="text-lg font-black text-slate-950 sm:text-xl">{title}</h3>
            <p className="text-xs font-semibold text-gray-400">{items.length} in library</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {groups.map((group) => (
          <ProviderMediaGroup
            key={`${title}-${group.providerName}`}
            group={group}
            color={color}
            variant={variant}
          />
        ))}
      </div>

      {emptyHint ? (
        <p className="text-xs leading-relaxed text-gray-400 sm:text-sm">{emptyHint}</p>
      ) : null}
    </section>
  );
}

function LockedMediaPanel({ color, ctaTo, isAuthenticated, topicLabel, onRetryInterest, onOpenSignup }) {
  return (
    <div className="flex min-h-[24rem] flex-col items-center justify-center rounded-[2rem] border border-dashed border-black/20 bg-white px-8 py-16 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}>
        <Lock size={28} strokeWidth={2} />
      </div>
      <h3 className="text-xl font-black text-gray-900">
        {isAuthenticated ? "Add this topic to unlock media" : "Sign in to watch & listen"}
      </h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-gray-500">
        {isAuthenticated ? (
          <>
            Videos and podcasts for <span className="font-semibold text-gray-700">{topicLabel}</span> are
            available only when this topic is part of your interests — so playback stays tailored to you.
          </>
        ) : (
          <>
            Register or sign in and choose <span className="font-semibold text-gray-700">{topicLabel}</span> as
            an interest to access provider videos and podcast episodes here.
          </>
        )}
      </p>
      {isAuthenticated ? (
        onRetryInterest ? (
          <button
            type="button"
            onClick={onRetryInterest}
            className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
            style={{ background: color, color: foregroundOnSolidAccent(color) }}
          >
            Add topic to my interests
            <ArrowRight size={14} />
          </button>
        ) : null
      ) : onOpenSignup ? (
        <button
          type="button"
          onClick={onOpenSignup}
          className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register &amp; get access
          <ArrowRight size={14} />
        </button>
      ) : (
        <Link
          to={ctaTo}
          className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register &amp; get access
          <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function TopicMediaTab({
  color,
  ctaTo,
  isAuthenticated,
  topicLabel,
  onRequestInterest,
  onOpenSignup,
  media,
}) {
  const subCategoryName = media.topicIds?.subCategoryName || topicLabel;

  if (!media.canAccess) {
    return (
      <LockedMediaPanel
        color={color}
        ctaTo={ctaTo}
        isAuthenticated={isAuthenticated}
        topicLabel={subCategoryName}
        onRetryInterest={onRequestInterest}
        onOpenSignup={onOpenSignup}
      />
    );
  }

  if (media.loading) {
    return (
      <div
        className="flex min-h-[20rem] flex-col items-center justify-center gap-3 rounded-[2rem] border border-black/[0.06] bg-white px-8 py-16 text-center"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loader2 className="h-9 w-9 animate-spin" style={{ color }} aria-hidden />
        <p className="text-sm font-semibold text-gray-600">Loading videos &amp; podcasts…</p>
      </div>
    );
  }

  if (media.error) {
    return (
      <TopicLibraryEmptyState kind="media" topicLabel={subCategoryName} color={color} />
    );
  }

  if (media.totalCount === 0) {
    return (
      <TopicLibraryEmptyState kind="media" topicLabel={subCategoryName} color={color} />
    );
  }

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-gray-400">Watch &amp; listen</p>
          <h2 className="mt-1 text-2xl font-black text-slate-950 sm:text-3xl">Videos &amp; podcasts</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500">
            Curated by verified providers in <span className="font-semibold text-gray-700">{subCategoryName}</span>.
            Playback is available only while you are signed in with this topic in your interests.
          </p>
        </div>
        <span
          className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black"
          style={{ background: `${color}18`, color }}
        >
          {media.totalCount} featured
        </span>
      </div>

      <MediaSection
        title="Videos"
        icon={Video}
        items={media.videos}
        color={color}
        variant="video"
      />

      <MediaSection
        title="Podcasts"
        icon={Headphones}
        items={media.podcasts}
        color={color}
        variant="podcast"
        emptyHint={
          media.podcasts.length
            ? "Episodes stream securely in the player — no need to copy embed links."
            : null
        }
      />
    </div>
  );
}
